"use client";

import { useCallback } from "react";
import { useTraceStore, type SpeedMode } from "@/store/useTraceStore";
import { PANEL } from "@/config/constants";

export default function TimeScrubber() {
  const phase = useTraceStore((s) => s.phase);
  const hops = useTraceStore((s) => s.hops);
  const activeHopIndex = useTraceStore((s) => s.activeHopIndex);
  const speedMode = useTraceStore((s) => s.speedMode);
  const setSpeedMode = useTraceStore((s) => s.setSpeedMode);
  const sidebarOpen = useTraceStore((s) => s.sidebarOpen);
  const setActiveHopIndex = useTraceStore((s) => s.setActiveHopIndex);
  const setPhase = useTraceStore((s) => s.setPhase);
  const setCameraMode = useTraceStore((s) => s.setCameraMode);

  const navigateToHop = useCallback(
    (index: number) => {
      setActiveHopIndex(index);
      if (phase === "complete") {
        setCameraMode("follow");
      }
    },
    [phase, setActiveHopIndex, setCameraMode]
  );

  const handleReplay = useCallback(() => {
    setActiveHopIndex(0);
    setPhase("animating");
    setCameraMode("departure");
  }, [setActiveHopIndex, setPhase, setCameraMode]);

  return (
    <div
      className="absolute bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 px-3 sm:px-5 py-2 sm:py-3 rounded-xl"
      role="navigation"
      aria-label="Journey timeline"
      style={{
        background: PANEL.BG,
        border: "1px solid rgba(255, 255, 255, 0.06)",
        boxShadow: "0 4px 30px rgba(0,0,0,0.4)",
        width: `min(640px, calc(100vw - ${sidebarOpen ? "420px" : "32px"}))`,
        maxWidth: "calc(100vw - 24px)",
      }}
    >
      {/* Timeline */}
      <div className="flex items-center gap-0.5 mb-2" role="group" aria-label="Journey hops">
        {hops.map((hop, i) => {
          const dotColor =
            hop.type === "origin"
              ? "#FFA726"
              : hop.type === "ixp"
                ? "#8B5CF6"
                : hop.type === "destination"
                  ? "#10B981"
                  : "#06B6D4";
          return (
            <div key={hop.num} className="flex items-center" style={{ flex: Math.max(hop.latency, 3) }}>
              <button
                onClick={() => navigateToHop(i)}
                aria-label={`Jump to hop ${hop.num}: ${hop.city}`}
                className="group relative cursor-pointer shrink-0"
              >
                <div
                  className="w-2.5 h-2.5 rounded-full transition-transform duration-200 group-hover:scale-150"
                  style={{ background: dotColor, opacity: i <= activeHopIndex ? 1 : 0.3 }}
                />
              </button>
              {i < hops.length - 1 && (
                <div
                  className="flex-1 mx-0.5 rounded-full"
                  aria-hidden="true"
                  style={{
                    background: hop.cable
                      ? "linear-gradient(90deg, #06B6D4, #0E7490)"
                      : i < activeHopIndex
                        ? "#06B6D4"
                        : "rgba(255,255,255,0.08)",
                    height: hop.cable ? "3px" : "2px",
                    opacity: i < activeHopIndex ? 0.5 : 0.2,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#71717A] font-[family-name:var(--font-geist-mono)]" aria-hidden="true">
          0ms
        </span>
        <div className="flex items-center gap-1" role="radiogroup" aria-label="Animation speed">
          {(["narrative", "fast", "realtime"] as SpeedMode[]).map((speed) => (
            <button
              key={speed}
              onClick={() => {
                setSpeedMode(speed);
                if (phase === "complete") handleReplay();
              }}
              role="radio"
              aria-checked={speedMode === speed}
              className={`px-3 py-1 rounded-full text-[11px] transition-all duration-200 cursor-pointer ${
                speedMode === speed
                  ? "bg-[#164E63] text-[#22D3EE] border border-[rgba(6,182,212,0.2)]"
                  : "text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              <span className="hidden sm:inline">{speed === "narrative" ? "Narrative" : speed === "fast" ? "Fast" : "Real-time"}</span>
              <span className="sm:hidden">{speed === "narrative" ? "1×" : speed === "fast" ? "3×" : "RT"}</span>
            </button>
          ))}
        </div>
        <span className="text-[11px] text-[#71717A] font-[family-name:var(--font-geist-mono)]" aria-hidden="true">
          {hops.length > 0 ? `${hops[hops.length - 1].cumLatency}ms` : ""}
        </span>
      </div>
    </div>
  );
}
